import { useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem,
    FormControl, FormControlLabel, Checkbox, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper, IconButton, Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import CustomSelect2 from '../CustomSelect2';
import NotesDisplay from '../NotesDisplay';
import RatesVersionSelect from '../RatesVersionSelect';
import PercentInput from '../PercentInput';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { LEAVE_BLANK, BLANK_QUOTE_RATES, BlankQuoteNotice, applyLeaveBlank, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

const PLANS = [
    { id: 'vumi_basic',    key: 'basic',    label: 'Basic' },
    { id: 'vumi_standard', key: 'standard', label: 'Standard' },
    { id: 'vumi_superior', key: 'superior', label: 'Superior' },
    { id: 'vumi_ultra',    key: 'ultra',    label: 'Ultra' },
    { id: 'vumi_total',    key: 'total',    label: 'Total' },
];

const ADD_ON_ID_KEYS = {
    extendedEvacuation: 'extended_evacuation',
    nonEmergencyEvacuation: 'non_emergency_evacuation',
    wellnessAndOptical500: 'wellness_and_optical_500',
    wellnessAndOptical1000: 'wellness_and_optical_1000',
    outpatientTreatment: 'outpatient_treatment',
};

const ADD_ON_CHECKBOXES = [
    { name: 'extendedEvacuation',     label: 'Extended Evacuation' },
    { name: 'nonEmergencyEvacuation', label: 'Non-Emergency Evacuation' },
    { name: 'wellnessAndOptical500',  label: 'Wellness and Optical ($500)' },
    { name: 'wellnessAndOptical1000', label: 'Wellness and Optical ($1000)' },
    { name: 'outpatientTreatment',    label: 'Outpatient Treatment ($5,000)' },
];

const DEFAULT_ADD_ONS = {
    extendedEvacuation: false,
    nonEmergencyEvacuation: false,
    wellnessAndOptical500: false,
    wellnessAndOptical1000: false,
    outpatientTreatment: false,
};

const PAYMENT_FREQUENCIES = ['Annually', 'Semi Annually', 'Quarterly', 'Monthly'];
const BRAND_COLOR = '#572d5f';
const ACCENT_COLOR = '#002047';

function getCurrencySign(currency) {
    if (currency === 'USD') return '$';
    if (currency === 'SGD') return 'S$';
    if (currency === 'EURO') return '€';
    if (currency === 'GBP') return '£';
    return '';
}

function formatRate(rate, currency) {
    if (rate === null || rate === undefined) return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return getCurrencySign(currency) + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Same as Vumi_Rates: discount/loading scale every plan rate
function getRateFactor(discount, loading) {
    return (100 - (parseFloat(discount) || 0) + (parseFloat(loading) || 0)) / 100;
}

export default function Vumi_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const areaOptions      = appConfig?.content?.vumi_area_of_coverage || [];
    const defaultArea = areaOptions.includes('Worldwide excl USA') ? 'Worldwide excl USA' : (areaOptions[0] || '');
    const deductibleOptions = Object.keys(appConfig?.content?.specifics?.vumi?.deductibles || {});
    const addOns            = appConfig?.content?.specifics?.vumi?.add_ons || {};
    const addOnOptions      = Object.keys(addOns);
    const coverageRestrictions = appConfig?.content?.specifics?.vumi?.coverage_restrictions || {};
    const outpatientTreatmentRate = appConfig?.content?.specifics?.vumi?.outpatient_treatment_rate_n_5000 || 0;
    const currencyOptions  = appConfig?.content?.currencies || ['USD'];

    const [selectedPlan, setSelectedPlan] = useState('vumi_basic');
    const [selectedAddOn, setSelectedAddOn] = useState('');
    const [vumiAddOns, setVumiAddOns] = useState(DEFAULT_ADD_ONS);
    const [areaOfCoverage, setAreaOfCoverage] = useState(defaultArea);
    const [currency, setCurrency]             = useState('USD');
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    const [discount, setDiscount] = useState('0');
    const [loadingPercent, setLoadingPercent] = useState('0');
    // { [quoteName]: deductible string }
    const [quoteDeductibles, setQuoteDeductibles] = useState({});
    const [deductibleErrors, setDeductibleErrors] = useState({});
    const [ratesData, setRatesData]   = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    const [loading, setLoading]       = useState(false);
    const initializingFromEdit = useRef(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    const selectedPlanObj = PLANS.find(p => p.id === selectedPlan) || PLANS[0];

    // Order here must match the combo keys in appConfig.content.specifics.vumi.add_ons —
    // this is the value sent to the backend (rates lookup, payload), so it cannot be reordered freely.
    function getVumiAddOnValue(currentAddOns) {
        const selected = [];
        if (currentAddOns.wellnessAndOptical500) selected.push('Wellness and Optical ($500)');
        else if (currentAddOns.wellnessAndOptical1000) selected.push('Wellness and Optical ($1000)');

        if (currentAddOns.extendedEvacuation) selected.push('Extended Evacuation');
        if (currentAddOns.nonEmergencyEvacuation) selected.push('Non-Emergency Evacuation');
        if (currentAddOns.outpatientTreatment) selected.push('Outpatient Treatment (- $5,000)');

        return selected.length > 0 ? selected.join(' + ') : 'None';
    }

    // Display-only ordering (matches the checkbox order in the UI) — used for the plan label shown
    // in the comparison table, kept separate from getVumiAddOnValue so the payload value is untouched.
    function getVumiAddOnDisplayLabel(currentAddOns) {
        const selected = [];
        if (currentAddOns.extendedEvacuation) selected.push('Extended Evacuation');
        if (currentAddOns.nonEmergencyEvacuation) selected.push('Non-Emergency Evacuation');
        if (currentAddOns.wellnessAndOptical500) selected.push('Wellness and Optical ($500)');
        else if (currentAddOns.wellnessAndOptical1000) selected.push('Wellness and Optical ($1000)');
        if (currentAddOns.outpatientTreatment) selected.push('Outpatient Treatment (- $5,000)');

        return selected.length > 0 ? selected.join(' + ') : 'None';
    }

    function parseVumiAddOnValue(value) {
        return {
            extendedEvacuation: !!value?.includes('Extended Evacuation'),
            nonEmergencyEvacuation: !!value?.includes('Non-Emergency Evacuation'),
            wellnessAndOptical500: !!value?.includes('Wellness and Optical ($500)'),
            wellnessAndOptical1000: !!value?.includes('Wellness and Optical ($1000)'),
            outpatientTreatment: !!value?.includes('Outpatient Treatment (- $5,000)'),
        };
    }

    function getCoverageRestriction(area) {
        return area && area !== 'Worldwide' ? 'Yes' : 'No';
    }

    // Same note as Vumi_Rates: "<x>% Discount for Coverage Restriction"
    function getCoverageRestrictionDiscount(area) {
        if (getCoverageRestriction(area) !== 'Yes') return '';
        const percent = coverageRestrictions[area];
        if (percent === undefined || percent === null) return '';
        return `${percent * 100}%`;
    }

    function getFormattedAddOnValue() {
        const addOnValue = addOns[selectedAddOn] ?? 0;
        const outpatientPercent = `${outpatientTreatmentRate * 100}%`;
        if (vumiAddOns.outpatientTreatment) {
            return addOnValue > 0 ? `($${addOnValue} + ${outpatientPercent})` : `(${outpatientPercent})`;
        }
        return `($${addOnValue})`;
    }

    const handleVumiAddOnChange = (name) => (e) => {
        const checked = e.target.checked;
        setVumiAddOns(prev => {
            const newState = { ...prev, [name]: checked };
            if (name === 'wellnessAndOptical500' && checked) newState.wellnessAndOptical1000 = false;
            if (name === 'wellnessAndOptical1000' && checked) newState.wellnessAndOptical500 = false;
            return newState;
        });
    };

    function getDefaultQuoteDeductibles() {
        const defaults = {};
        (quotes || []).forEach(q => {
            defaults[q.name] = deductibleOptions[0] || '';
        });
        return defaults;
    }

    // Pre-populate form when opening
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            initializingFromEdit.current = true;
            setSelectedPlan(editingPlan.base_plan_id || editingPlan.plan_id);
            setVumiAddOns(parseVumiAddOnValue(editingPlan.payloads?.add_on));
            setAreaOfCoverage(editingPlan.payloads?.area_of_coverage || defaultArea);
            setCurrency(editingPlan.payloads?.currency           || 'USD');
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');
            const savedDiscount = editingPlan.payloads?.discount ?? '0';
            const savedLoading = editingPlan.payloads?.loading ?? '0';
            setDiscount(String(savedDiscount));
            setLoadingPercent(String(savedLoading));
            const savedFactor = getRateFactor(savedDiscount, savedLoading);
            // Undo the saved discount/loading so ratesData holds the raw rate again
            const unscale = val => (typeof val === 'number' && savedFactor > 0 ? val / savedFactor : val);

            const deductibles = {};
            (quotes || []).forEach(quote => {
                deductibles[quote.name] = editingPlan.deductibles_per_quote?.[quote.uid] || '';
            });
            setQuoteDeductibles(deductibles);

            // Reconstruct ratesData from stored premiums
            const ratesMap = {};
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                // Match the specific plan instance (plan_uid) so editing one of several same-tier
                // plans doesn't pull rates from a sibling; fall back to plan_id for plans saved
                // before per-instance uids existed.
                const rawRates = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(p =>
                        editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id
                    );
                    return Object.fromEntries(PLANS.map(plan => [plan.key, unscale(premium?.[`${plan.key}_premium`] ?? null)]));
                });
                ratesMap[quote.uid] = { rawRates };
            });
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
            setTimeout(() => { initializingFromEdit.current = false; }, 0);
        } else if (open && !editingPlan) {
            setVumiAddOns(DEFAULT_ADD_ONS);
            setAreaOfCoverage(defaultArea);
            setCurrency('USD');
            setPaymentFrequency('Annually');
            setDiscount('0');
            setLoadingPercent('0');
            setQuoteDeductibles(getDefaultQuoteDeductibles());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Combine add-on checkboxes into the underlying value string. The add-on is part of the
    // rates request, so changing it invalidates the shown rates.
    useEffect(() => {
        setSelectedAddOn(getVumiAddOnValue(vumiAddOns));
        if (initializingFromEdit.current) return;
        invalidateRates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vumiAddOns]);

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return quoteDeductibles[quote.name] === LEAVE_BLANK;
    }

    async function handleViewRates() {
        const errors = {};
        (quotes || []).forEach(q => {
            if (!quoteDeductibles[q.name]) errors[q.name] = true;
        });
        if (Object.keys(errors).length > 0) {
            setDeductibleErrors(errors);
            const firstQuote = (quotes || []).find(q => errors[q.name]);
            toast.error(`Please select a deductible for "${firstQuote?.name}"`, { hideProgressBar: true, autoClose: 3000 });
            return;
        }
        setDeductibleErrors({});
        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => isBlankQuote(quote)
                    ? Promise.resolve({ quoteUid: quote.uid, quote, blank: true })
                    : getRates({
                        token: auth.token,
                        body: {
                            clients,
                            deductibles: { vumi: quoteDeductibles[quote.name] || '' },
                            insurers: ['vumi'],
                            specifics: {
                                vumi: {
                                    deductible: quoteDeductibles[quote.name] || '',
                                    coverage_restriction: getCoverageRestriction(areaOfCoverage),
                                    add_on: selectedAddOn,
                                },
                            },
                            specific_area_of_coverage:  { vumi: areaOfCoverage },
                            rates_version_per_insurer:  { vumi: selectedRatesVersion?.vumi || '' },
                            specific_currency:          { vumi: currency },
                            specific_payment_frequency: { vumi: paymentFrequency },
                        },
                    }).then(response => ({ quoteUid: quote.uid, data: response?.data?.vumi }))
                )
            );

            const ratesMap = {};
            results.forEach(({ quoteUid, data, blank }) => {
                if (blank) {
                    ratesMap[quoteUid] = BLANK_QUOTE_RATES;
                    return;
                }
                if (data?.rates) {
                    const rawRates = data.rates.map(r => Object.fromEntries(PLANS.map(plan => [plan.key, r[plan.key]])));
                    ratesMap[quoteUid] = { rawRates };
                } else {
                    ratesMap[quoteUid] = data;
                }
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching Vumi rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    const rateFactor = getRateFactor(discount, loadingPercent);

    // ratesData keeps the raw API rates for every plan; discount/loading are applied on read
    function getPlanRate(clientIndex, planKey, quoteUid) {
        const rate = ratesData?.[quoteUid]?.rawRates?.[clientIndex]?.[planKey];
        if (rate === undefined || rate === null) return null;
        return typeof rate === 'number' ? rate * rateFactor : rate;
    }

    function getRate(clientIndex, quoteUid) {
        return getPlanRate(clientIndex, selectedPlanObj.key, quoteUid);
    }

    function getSubtotal(planKey, quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return null;
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getPlanRate(i, planKey, quoteUid);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    function buildPlanLabel() {
        const displayAddOn = getVumiAddOnDisplayLabel(vumiAddOns);
        return displayAddOn && displayAddOn !== 'None'
            ? `${selectedPlanObj.label} + ${displayAddOn}`
            : selectedPlanObj.label;
    }

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;

        const planLabel = buildPlanLabel();
        const activeAddOnKeys = Object.keys(vumiAddOns).filter(k => vumiAddOns[k]).map(k => ADD_ON_ID_KEYS[k] || k);
        const addOnsSuffix = activeAddOnKeys.join('_');
        const effectivePlanId = editingPlan
            ? editingPlan.plan_id
            : (addOnsSuffix ? `${selectedPlan}_${addOnsSuffix}` : selectedPlan);
        // plan_id identifies the tier + add-on combination and is shared by every instance of it;
        // uid identifies this specific plan instance so multiple plans of the same combination
        // can coexist instead of overwriting each other. Editing reuses the existing plan's uid.
        const planUid = editingPlan ? editingPlan.uid : uuidv4();

        const updates = [];
        (quotes || []).forEach(quote => {
            clients.forEach((client, clientIndex) => {
                const rate = getRate(clientIndex, quote.uid);
                // Stored with discount/loading applied, matching premium_rate
                const planPremiums = Object.fromEntries(PLANS.map(plan => [
                    `${plan.key}_premium`,
                    getPlanRate(clientIndex, plan.key, quote.uid),
                ]));
                updates.push({
                    quoteUid: quote.uid,
                    clientUid: client.uid,
                    planId: effectivePlanId,
                    planUid,
                    premium: {
                        insurer: 'vumi',
                        plan: selectedPlanObj.key,
                        plan_label: planLabel,
                        included_benefits: '',
                        deductible: quoteDeductibles[quote.name] || '',
                        area_of_coverage: areaOfCoverage,
                        coverage_restriction: getCoverageRestriction(areaOfCoverage),
                        currency,
                        payment_frequency: paymentFrequency,
                        add_on: selectedAddOn,
                        discount,
                        loading: loadingPercent,
                        premium_rate: rate ?? null,
                        ...planPremiums,
                    },
                });
            });
        });

        const plan = {
            plan_id: effectivePlanId,
            uid: planUid,
            base_plan_id: selectedPlan,
            plan: selectedPlanObj.key,
            insurer: 'vumi',
            label: `VUMI Global ${planLabel}`,
            payloads: {
                area_of_coverage: areaOfCoverage,
                coverage_restriction: getCoverageRestriction(areaOfCoverage),
                currency,
                payment_frequency: paymentFrequency,
                add_on: selectedAddOn,
                discount,
                loading: loadingPercent,
            },
            deductibles_per_quote: Object.fromEntries(
                (quotes || []).map(quote => [quote.uid, quoteDeductibles[quote.name] || ''])
            ),
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    const selectSx = { fontSize: 12, height: 32 };
    const menuItemSx = { fontSize: 12 };
    const coverageRestrictionDiscount = getCoverageRestrictionDiscount(areaOfCoverage);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: BRAND_COLOR, px: 2, py: 1 }}>
                <div style={{
                    color: '#fff',
                    backgroundColor: '#3f1b46',
                    width: 100,
                    height: 60,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 13,
                    lineHeight: 1.2,
                }}>VUMI GLOBAL FLEX VIP</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <img src={`${process.env.PUBLIC_URL}/images/Vumi Logo.png`} alt="Vumi" style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 2, mt: 3 }}>
                {/* Plan Type */}
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                            value={selectedPlan}
                            disabled={loading || !!editingPlan}
                            // Rates for every plan come back in one fetch, so switching plans just shows a different row
                            onChange={(e) => setSelectedPlan(e.target.value)}
                            sx={selectSx}
                        >
                            {PLANS.map(plan => (
                                <MenuItem key={plan.id} value={plan.id} sx={menuItemSx}>{plan.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Add On */}
                {addOnOptions.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2, maxWidth: 320 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 15 }}>
                            Add On
                            {selectedAddOn !== 'None' && (
                                <span style={{ marginLeft: 6, fontSize: 15, color: ACCENT_COLOR, fontWeight: 600 }}>{getFormattedAddOnValue()}</span>
                            )}
                        </Typography>
                        {ADD_ON_CHECKBOXES.map(addOn => (
                            <FormControlLabel
                                key={addOn.name}
                                sx={{ mb: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                                control={<Checkbox size="small" checked={vumiAddOns[addOn.name]} disabled={loading || !!editingPlan} onChange={handleVumiAddOnChange(addOn.name)} />}
                                label={addOn.label}
                            />
                        ))}
                        {(vumiAddOns.wellnessAndOptical500 || vumiAddOns.wellnessAndOptical1000 || vumiAddOns.outpatientTreatment) && (
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {vumiAddOns.outpatientTreatment && (
                                    <li>
                                        <Typography fontSize={12} color={BRAND_COLOR}><strong>Outpatient Treatment</strong> is only available in <strong>Basic Plan</strong></Typography>
                                    </li>
                                )}
                                {(vumiAddOns.wellnessAndOptical500 || vumiAddOns.wellnessAndOptical1000) && (
                                    <li>
                                        <Typography fontSize={12} color={BRAND_COLOR}><strong>Wellness and Optical</strong> is not available in <strong>Basic Plan</strong></Typography>
                                    </li>
                                )}
                            </ul>
                        )}
                    </Box>
                )}

                {/* Options — label on the left, select on the right */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px', columnGap: 3, rowGap: 1.5, alignItems: 'start', alignContent: 'start', mb: 2 }}>
                        {areaOptions.length > 0 && (
                            <>
                                <Typography fontWeight={600} sx={{ fontSize: 13, lineHeight: '32px' }}>Area Of Coverage</Typography>
                                <Box>
                                    <FormControl size="small" sx={{ width: 170 }}>
                                        <Select value={areaOfCoverage} disabled={loading} onChange={(e) => { setAreaOfCoverage(e.target.value); invalidateRates(); }} sx={selectSx}>
                                            {areaOptions.map(area => <MenuItem key={area} value={area} sx={menuItemSx}>{area}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    {coverageRestrictionDiscount && (
                                        <Typography fontSize={12} sx={{ mt: 1, color: ACCENT_COLOR, fontWeight: 600 }}>
                                            {coverageRestrictionDiscount} Discount for Coverage Restriction
                                        </Typography>
                                    )}
                                </Box>
                            </>
                        )}
                        {currencyOptions.length > 0 && (
                            <>
                                <Typography fontWeight={600} sx={{ fontSize: 13, lineHeight: '32px' }}>Currency</Typography>
                                <FormControl size="small" sx={{ width: 170 }}>
                                    <Select value={currency} disabled={loading} onChange={(e) => { setCurrency(e.target.value); invalidateRates(); }} sx={selectSx}>
                                        {currencyOptions.map(c => <MenuItem key={c} value={c} sx={menuItemSx}>{c}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </>
                        )}
                        <Typography fontWeight={600} sx={{ fontSize: 13, lineHeight: '32px' }}>Payment Frequency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value={paymentFrequency} disabled={loading} onChange={(e) => { setPaymentFrequency(e.target.value); invalidateRates(); }} sx={selectSx}>
                                {PAYMENT_FREQUENCIES.map(f => <MenuItem key={f} value={f} sx={menuItemSx}>{f}</MenuItem>)}
                            </Select>
                        </FormControl>
                </Box>

                {/* Renewal/Further Discount & Loading */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Discount</Typography>
                        <PercentInput
                            value={discount}
                            onCommit={setDiscount}
                            disabled={loading}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Loading</Typography>
                        <PercentInput
                            value={loadingPercent}
                            onCommit={setLoadingPercent}
                            disabled={loading}
                        />
                </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Deductibles per Quote */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>Deductibles per Quote</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 200 }}>{quote.name}</Typography>
                                <CustomSelect2
                                    dense
                                    value={quoteDeductibles[quote.name] || ''}
                                    onChange={(e) => {
                                        setQuoteDeductibles(prev => ({ ...prev, [quote.name]: e.target.value }));
                                        setDeductibleErrors(prev => ({ ...prev, [quote.name]: false }));
                                        invalidateRates();
                                    }}
                                    items={[LEAVE_BLANK, ...deductibleOptions]}
                                    disabled={loading}
                                    error={!!deductibleErrors[quote.name]}
                                    sx={{ minWidth: 200 }}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {/* Loading */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: BRAND_COLOR }} />
                    </Box>
                )}

                {/* Rates Display */}
                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ mb: 3 }}>
                                <Typography fontWeight={600} sx={{ mb: 1 }}>
                                    {quote.name}
                                    <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 13 }}>
                                        — {quoteDeductibles[quote.name] || '-'}
                                        {selectedAddOn && selectedAddOn !== 'None' ? ` | Add-On: ${selectedAddOn}` : ''}
                                    </Typography>
                                </Typography>
                                {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: BRAND_COLOR }}>
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 190 }}>Plan</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>
                                                        <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                        <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                                                <TableCell sx={{ fontWeight: 500 }}>{buildPlanLabel()}</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx}>
                                                        {formatRate(getRate(idx, quote.uid), currency)}
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ fontWeight: 600 }}>
                                                    {formatRate(getSubtotal(selectedPlanObj.key, quote.uid), currency)}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Paper>
)}
                            </Box>
                        ))}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId="vumi" />

                <RatesVersionSelect
                    insurer="vumi"
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />

                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}
